import { canReadAndWrite } from '@visa/lib-cookie';
import { GtmInstr } from '../../src-i-adapter/helpers/gtm';
import { NONE, apiErrors, apiMethodnames } from '../constants/common';
import { AMEX, DISCOVER, MA, MASTERCARD, VISA } from '../constants/partnering-networks';
import { VISA_SDK_URI } from '../constants/sdk-load-param';
import collapsedLogger from '../helpers/collapsed-logger';
import generateInitData from '../helpers/generate-init-data';
import getCardBrands from '../helpers/get-card-brands';
import getDomain from '../helpers/get-domain';
import getFilteredSdks from '../helpers/get-filtered-sdks';
import getFormattedSRCNetworks from '../helpers/get-formatted-src-networks';
import getIsRecognizedPromises from '../helpers/get-is-recognized-promises';
import getSuccessfulNetworksCount from '../helpers/get-successful-networks-count';
import getSupportedCardBrands from '../helpers/get-supported-card-brands';
import { trackRecognizedUser, trackVsbInitialize } from '../helpers/gtm';
import { isAmexSdk, isDiscoverSdk, isMaSdk, isVisaSdk, loadScript } from '../helpers/load-script';
import {
  flushEvents,
  promiseTimer,
  pushAnyEvent,
  pushInitEvent,
  setVco
} from '../helpers/log-batch';
import logger from '../helpers/logger';
import mapVinitToIntentPayload from '../helpers/map-vinit-to-intent-payload';
import OrchCustomData from '../helpers/orch-custom-data';
import promiseTimeout from '../helpers/promise-timeout';
import pushUpdatedVInit from '../helpers/push-updated-vinit';
import StaticSingletonClass from '../helpers/static-id';
import isRecognized from './is-recognized';
import VsbOrchAdapter from './index';

const promiseWrapper = function(instance, instanceName, initData, method) {
  if (instance === null || instance === undefined) {
    const resultData = {
      [instanceName]: {
        error: apiErrors.NO_INSTANCE
      }
    };
    return Promise.resolve(resultData);
  }

  collapsedLogger(`Initializing ${instanceName}`, initData);
  return promiseTimer(instance, method, initData, instanceName, pushInitEvent)
    .then(res => {
      logger(`Initialized ${instanceName}`);
      // TODO: remove this config once we sunset VCO branding.
      // Our backend needs to know the branding in order to trigger the correct type of emails.
      // prettier-ignore
      const isRecognizedData =
        instanceName === VISA
          ? { branding: initData.customData.configs.initialBranding }
          : {};
      const isRecognizedPromise = isRecognized(instance, instanceName, isRecognizedData);
      // on init success, push network name to init for V.isRecognized response supportedCardNetworks
      OrchCustomData.initSuccessfulNetworks.push(instanceName === MA ? MASTERCARD : instanceName);
      return { isError: false, isRecognizedPromise, network: instanceName, response: res };
    })
    .catch(err => {
      collapsedLogger(`Error Initializing ${instanceName}`, err);
      // vsb init
      OrchCustomData.setLoadedSdk({
        [instanceName]: null
      });
      OrchCustomData.removeNetwork(instanceName);
      return { initData, isError: true, network: instanceName, response: err };
    });
};

export const initSdk = function(configs, sdkInstance, network, apiKey) {
  const { INIT } = apiMethodnames;
  const initData = generateInitData(configs, network);
  const timeoutError = { isError: true, network, response: '' };

  // save all networks initData to OrchCustomData
  OrchCustomData.initData[network] = initData;

  if (network !== VISA) {
    initData.srcInitiatorId = apiKey;
    // F23316: All non-VISA networks will have tokenization disabled and will send PAN only
    // when metadata feature toggle flag : isTokenizationEnabled === true
    if (OrchCustomData.isTokenizationEnabled) {
      initData.dpaTransactionOptions.paymentOptions.dpaPanRequested = true;
    }
  }

  if (network === AMEX) {
    const transactionAmount = initData?.dpaTransactionOptions?.transactionAmount?.transactionAmount;
    if (typeof transactionAmount === 'boolean' && !transactionAmount) {
      delete initData.dpaTransactionOptions.transactionAmount;
    }
  }

  if (network === DISCOVER) {
    initData.srcDpaId = initData.srciDpaId;
    //threeDsPreference not supported by Discover. NONE to be passed
    initData.dpaTransactionOptions.threeDsPreference = NONE;
    if (initData?.dpaTransactionOptions?.hasOwnProperty('paymentOptions')) {
      const {
        dpaTransactionOptions: { paymentOptions }
      } = initData;
      const { dpaPanRequested } = paymentOptions;
      logger(`dpaPanRequested ${dpaPanRequested}`);
      //remove custom implementation for Discover
      //to match as per spec(Discover 1.1.1)
      if (paymentOptions?.dpaPanRequested && paymentOptions?.dynamicDataType === null) {
        paymentOptions.dynamicDataType = NONE;
      }
      initData.dpaTransactionOptions.paymentOptions = Array(paymentOptions);
    }
    delete initData.srciDpaId;
  }

  let initPromise = promiseWrapper(sdkInstance, network, initData, INIT);

  // VCO-14858 - In Visa SRCi integrations, the Visa SDK MUST be initialized for
  // the app to work. It is necessary to ensure that .init() completes without
  // timing out to avoid unknown bugs.
  if (network !== VISA) {
    initPromise = promiseTimeout(initPromise, timeoutError);
  }

  return initPromise;
};

/**
 * Initialize Visa/Non-Visa SDK
 *  - this will be called for each script to load and initialize the sdk.
 *  - gets the network instance and calls init() on it
 * @param {object} configs
 * @param {string} cardBrand sdkCardBrand
 * @param {string} apiKey
 * @returns {Promise} init promise
 */
const _initiateSDK = (configs, cardBrand, apiKey) => {
  let network;
  let sdkInstance;
  const { vInitRequest } = configs;
  const payload =
    vInitRequest && mapVinitToIntentPayload(JSON.parse(JSON.stringify(vInitRequest)), cardBrand);
  OrchCustomData.intentRequestPayload = payload;
  if (isVisaSdk(cardBrand)) {
    network = VISA;
    try {
      const VCOSdk = window.vAdapters.VisaSRCI;
      const srcInitiatorId = OrchCustomData.srcInitiatorId.VISA;
      sdkInstance = new VCOSdk();
      OrchCustomData.setLoadedSdk({
        [network]: sdkInstance
      });
      // for CALogger batch send
      setVco(sdkInstance);

      pushAnyEvent({
        E: `ORCH_INIT_${network}_CANREADANDWRITECOOKIE`,
        ERRCODE: null,
        ERRMSG: null,
        LL: 'INFO',
        MSG: JSON.stringify({
          canReadAndWriteCookies: canReadAndWrite()
        }),
        checkout_step: `ORCH_INIT_${network}_CANREADANDWRITECOOKIE`,
        srcInitiatorId
      });
    } catch (e) {
      OrchCustomData.removeNetwork(network.toUpperCase());
    }
  } else if (isMaSdk(cardBrand)) {
    network = MA;
    try {
      sdkInstance = window.SRCSDK_MASTERCARD;
      OrchCustomData.setLoadedSdk({
        [network]: sdkInstance
      });
    } catch (e) {
      OrchCustomData.removeNetwork(network.toUpperCase());
    }
  } else if (isAmexSdk(cardBrand)) {
    network = AMEX;
    try {
      sdkInstance = window.AmexSS.getInstance();
      OrchCustomData.setLoadedSdk({
        [network]: sdkInstance
      });
    } catch (e) {
      OrchCustomData.removeNetwork(network.toUpperCase());
    }
  } else if (isDiscoverSdk(cardBrand)) {
    network = DISCOVER;
    try {
      sdkInstance = window.DGNCheckout;
      OrchCustomData.setLoadedSdk({
        [network]: sdkInstance
      });
    } catch (e) {
      OrchCustomData.removeNetwork(network.toUpperCase());
    }
  }

  //Invoking Orch init(). This will inturn invoke all SDK init() SDK API
  return initSdk(configs, sdkInstance, network, apiKey);
};

/**
 * Init all sdks and return a promise.all of their response
 * @param {object} configs
 * @return {promise} promise array of init promises
 */
const loadAndInitSdks = function(configs) {
  logger('configs ==> ', configs);
  VsbOrchAdapter.instance.configs = configs;
  //debug true is needed for logger to enable. We also restrict logger in production environemnt
  StaticSingletonClass.debug = true;
  //Visa SDK will be loaded always when Visa as SRCi
  const visaDomain = getDomain();
  const visaSdkUrl = `${visaDomain}${VISA_SDK_URI}?visaSrci=true`;
  let filteredNetworkPromises = [];

  const networkID = `${VISA.toLowerCase()}SDK`;
  const existingVISASDKScript = document.getElementById(networkID);
  if (existingVISASDKScript) {
    const { isMultiVInitUpdated } = pushUpdatedVInit(configs);
    VsbOrchAdapter.instance.isMultiVInitUpdated = isMultiVInitUpdated;
    VsbOrchAdapter.instance.vInitRequestUpdated = OrchCustomData.currVInitConfigs;
    // fetch cached init response from previous call and return it
    return Promise.resolve(
      OrchCustomData.initSuccessfulNetworks.length > 0 ? [{ isError: false }] : [{ isError: true }]
    );
  }
  if (configs && configs.vInitRequest) {
    OrchCustomData.currVInitConfigs = Object.assign({}, configs.vInitRequest);
  }

  const visaScriptPromise = loadScript(visaSdkUrl, VISA);

  OrchCustomData.srcInitiatorId.VISA = configs.vInitRequest && configs.vInitRequest.apikey;
  // update showVdcpIno flag in OrchCustomData base on config
  if (typeof configs.isInteropEligible === 'boolean') {
    OrchCustomData.setShowVdcpIno(configs.isInteropEligible);
  }
  const srcNetworks = configs?.vInitRequest?.srcNetworks ?? [];
  const srcFutEnabled = configs?.vInitRequest?.srcNetworksFut?.enabled ?? false;
  //check if srcInfo exists in the object to load ext sdks
  if (configs.hasOwnProperty('srcInfo') && configs.srcInfo !== null) {
    const { sdks } = configs.srcInfo;
    /* Return: filteredSdks:
     *  - if futEnabled || srcNetworks === (ALL || '') : srcInfo.sdks
     *  - else intersection between srcInfo.sdks and srcNetworks
     */
    const filteredSdks = getFilteredSdks(sdks, srcNetworks, srcFutEnabled);
    if (filteredSdks && filteredSdks.length) {
      //retrieve supportedCardBrands if any in vInit/merchantProfile response and load supported SDKs
      const vInitCardBrands = configs?.vInitRequest?.payment?.cardBrands ?? [];
      const merchantProfileBrands = getCardBrands(configs.merchantConfig);
      const isVsb = OrchCustomData.isVsb;
      /*eslint-disable no-console*/
      const cardBrands =
        Array.isArray(configs.buttonCardBrands) && configs.buttonCardBrands.length
          ? configs.buttonCardBrands
          : merchantProfileBrands;
      //while considering button cardbrands to support FUT usecase load all the brands from srcInfo
      const supportedCardBrands = getSupportedCardBrands(
        cardBrands,
        sdks,
        vInitCardBrands,
        getFormattedSRCNetworks(srcNetworks),
        srcFutEnabled,
        isVsb
      );
      filteredNetworkPromises = filteredSdks
        .filter(sdk => supportedCardBrands.indexOf(sdk.cardBrand) > -1 && sdk.cardBrand !== VISA)
        .map(sdk => {
          const key = sdk.cardBrand === MASTERCARD ? MA : sdk.cardBrand;
          OrchCustomData.srcInitiatorId[key] = sdk.apiKey;
          return loadScript(sdk.sdkUri, sdk.cardBrand, sdk.apiKey);
        });
    }
  }
  return Promise.all([visaScriptPromise, ...filteredNetworkPromises]);
};

/**
 * VSB init
 *  - called by merchant sdk to load, call init, isRecognized for all networks
 * @param {object} configs
 * @return {promise} init response isInitCompleted
 */
const init = function(configs) {
  /*
   * Vsb flags set based on what merchant calls:
   * V.initializeVsb: isVsb=true, isVsbButtonless=true. if then calls V.init: isVsb=true, isVsbButtonless=false
   * only V.init: isVsb=false, isVsbButtonless=false.
   */
  const isVsb = configs.isVsb && configs.isVsbEnabled;
  if (isVsb) {
    // set to true if initializevsb ever called once
    OrchCustomData.setVsbFlag(true);
  }
  // true if initializeVsb last called, false if vinit last called
  OrchCustomData.setVsbButtonlessFlag(configs.isVsbButtonless && configs.isVsbEnabled);
  VsbOrchAdapter.instance.isVsbSdkLoadComplete = false;
  const isTokenizationEnabled = Boolean(configs.isTokenizationEnabled);
  OrchCustomData.setTokenizationEnabled(isTokenizationEnabled);

  return new Promise(resolve => {
    loadAndInitSdks({
      ...configs,
      isVsb: OrchCustomData.isVsb,
      isVsbButtonless: OrchCustomData.isVsbButtonless
    })
      .then(async responses => {
        const initArray = [];
        for (const resp of responses) {
          if (!resp.error) {
            initArray.push(_initiateSDK(configs, resp.network, resp.apiKey));
          } else {
            logger(`Failed to initialize ${resp.network} SRC SDK`, resp);
          }
        }

        const initResponses = await Promise.all(initArray);

        logger(
          'VSB SLA: time from initializeVsb() to all network promise resolve:',
          (Date.now() - configs.initializeVsbStartTime) / 1000
        );

        getIsRecognizedPromises(initResponses)
          .then(promiseResponses => {
            // call orchReady method on Merchant SDK
            window.V?.orchReady?.();

            for (const resp of initResponses) {
              if (resp.isError) {
                GtmInstr.trackInitInitiation(resp.initData, 'Response', 'Error');
              }
            }

            const isNetworkRecognized = promiseResponses.some(resp => {
              return resp.res?.recognized === true;
            });

            if (!isNetworkRecognized) {
              //VCO-15255 - the last object in promiseResponses sets gtm state. If not recognized default to Visa.
              promiseResponses.sort(a => (a.network === 'VISA' ? 1 : -1));
            } else {
              //VCO-15255 - the last object in promiseResponses sets gtm state. If recognized set to correct network.
              promiseResponses.sort(a => (a.res?.recognized ? 1 : -1));
            }

            for (const resp of promiseResponses) {
              if (!OrchCustomData.isVsb) {
                trackRecognizedUser(
                  { [resp.network]: resp.network },
                  OrchCustomData,
                  {
                    isError: false,
                    isRequest: false,
                    isVsb: false,
                    usedDevice: resp?.usedDevice ?? false
                  },
                  resp?.res?.recognized
                );
              }
            }

            // batch up CALogger requests
            flushEvents();
          })
          .catch(err => {
            logger('Failed isRecognized for SRC SDKs', err);
          });

        // get list of networks that initialized.  Need at least one sdk to successfully load and init
        const successfulNetworksCount = getSuccessfulNetworksCount(responses);
        if (successfulNetworksCount > 0) {
          if (isVsb) {
            trackVsbInitialize(OrchCustomData, false);
            resolve({ isInitCompleted: true });
          } else {
            resolve();
          }
        } else {
          logger('Failed to initialize SRC SDKs');
          if (isVsb) {
            trackVsbInitialize(OrchCustomData, true);
            resolve({ isInitCompleted: false });
          } else {
            resolve();
          }
        }
      })
      .catch(err => {
        logger('Failed to initialize SRC SDKs', err);
        if (isVsb) {
          trackVsbInitialize(OrchCustomData, true);
          resolve({ isInitCompleted: false });
        } else {
          resolve();
        }
      });
  });
};

export default init;
